import axios from 'axios';
import router from '@/plugins/router';
import { utilityService } from '@/services/utilityService.js';
import { authTokenService } from '@/services/authTokenService.js';
import { Roles } from '@/common/Roles.js';

/**
 * Service to interact with the authentication api.
 */
export const authService = {
	login,
	logout,
	resetPassword,
	changePassword,
	forgotPassword,
	isAuthenticated,
	changeRespondentPassword,
	changeUserPassword,
	updateLockStatus,
	getShouldChangePassword,
	loginSso
};

/**
 * Logs the user in. This is to be used only for development to support an internal login.
 * @param {string} userName
 * @param {*} password
 * @returns the promise
 */
function login (userName, password) {
	return new Promise(function (resolve, reject) {
	// Clear any access_token values that may be floating around in local storage
		authTokenService.clearSession();

		axios.post(utilityService.getApiAuthUrl('/auth/external'),
			// Second parameter is the data/body
			{ UserName: userName, Password: password },
			// Third parameter is the configuration object
			{ headers: utilityService.getHeaders() })
			.then(function (response) {
				authTokenService.saveToken(response.data.token);
				if (!authTokenService.isInRole([Roles.Researcher])) {
					authTokenService.clearSession();
					reject(new Error('You are not authorized to view this site.'));
				} else {
					resolve();
				}
			})
			.catch(function (e) {
				reject(new Error('There was an error logging in. Please try again.'));
			});
	});
}

/**
 * Stores the token in the browser session storage.
 * @param {*} token The auth token
 * @returns A promise that resolves when the token is saved.
 */
function loginSso (token) {
	return new Promise(function (resolve, reject) {
		authTokenService.clearSession();
		authTokenService.saveToken(token);

		// Verify the use has the correct role to enter the site
		const tokenClaims = authTokenService.getClaims();
		if (!tokenClaims.roles.includes(Roles.Researcher)) {
			authTokenService.clearSession();
			const e = { message: 'You are not authorized to view this site.' }
			// Instead of throwing an exception we can just reject the promise
			reject(e);
		} else {
			resolve();
		}
	});
}

/**
 * Changes the user's password.
 * @param {string} userName
 * @param {string} oldPassword
 * @param {string} newPassword
 * @param {string} confirmPassword
 */
function changePassword (userName, oldPassword, newPassword, confirmPassword) {
	// Clear any access_token values that may be floating around in local storage

	return axios.put(utilityService.getApiAuthUrl('user/password'),
		// Second parameter is the data/body
		{ UserId: userName, OldPassword: oldPassword, NewPassword: newPassword, ConfirmPassword: confirmPassword },
		// Third parameter is the configuration object
		{
			headers: utilityService.getHeaders()
		}
	)
		.then(function (response) {
			// authService.login(userName, newPassword);
		})
		.catch(function (e) {
			console.error(e);
			throw e;
		}
		);
}

/**
 * Resets the user's password.
 * @param {string} userName
 * @param {string} confirmPassword
 */
function resetPassword (newPassword, confirmPassword) {
	// Clear any access_token values that may be floating around in local storage

	return axios.put(utilityService.getApiUrl('user/resetpassword'),
		// Second parameter is the data/body
		{ NewPassword: newPassword, ConfirmPassword: confirmPassword },
		// Third parameter is the configuration object
		{
			headers: utilityService.getHeaders()
		}
	)
		.then(function (response) {
			// authService.login(userName, newPassword);
		})
		.catch(function (e) {
			console.error(e);
			throw e;
		}
		);
}

function forgotPassword (userNameOrEmail) {
	// Clear any access_token values that may be floating around in local storage

	return axios.post(utilityService.getApiUrl('user/forgotpassword'),
		// Second parameter is the data/body
		{ UserNameOrEmail: userNameOrEmail },
		// Third parameter is the configuration object
		{
			headers: utilityService.getHeaders()
		}
	)
		.then(function (response) {
			// authService.login(userName, newPassword);
		})
		.catch(function (e) {
			console.error(e);
			throw e;
		}
		);
}

/**
 * Logs the user out by clearing the token data from the browser.
 */
function logout () {
	authTokenService.clearSession();

	if (!utilityService.getDisplayLoginForm()) {
		location.replace(utilityService.getLoginUrl());
	} else {
		router.push('/login');
	}
}

/**
 * Returns true if the user has a token stored in local storage.
 */
function isAuthenticated () {
	return authTokenService.getToken() != null;
}

/**
 * Resets the respondents password
 * @param {Object} body - the body of the request
 * @param {Object} body.newPassword - the new password value
 * @param {UUID} userId - the user id
 * @returns {Promise} - the promise from the post call
 */
function changeRespondentPassword (body = { newPassword }, userId) {
	return axios.post(utilityService.getApiAuthUrl(`/users/${userId}/passwords`),
		body,
		{
			headers: utilityService.getHeaders()
		}
	)
}

function changeUserPassword (body = { newPassword, oldPassword }, userId) {
	return axios.post(utilityService.getApiAuthUrl(`/users/${userId}/passwords`),
		body,
		{
			headers: utilityService.getHeaders()
		}
	)
}

function updateLockStatus (userId, isLocked) {
	return axios.post(utilityService.getApiAuthUrl(`/users/${userId}/locked`),
		{ IsLocked: isLocked },
		{
			headers: utilityService.getHeaders(false)
		}
	);
}

/**
 * Gets the shouldChangePassword flag. If it is not stored in session retrieve from the
 * Authentication API. Otherwise track it locally.
 * @returns {boolean} - true if the user should change their password
 */
async function getShouldChangePassword () {
	let shouldChangePassword = authTokenService.getShouldChangePassword();
	if (shouldChangePassword === null) {
		const response = await __getShouldChangePassword(authTokenService.getClaims().userId);
		shouldChangePassword = response.data.shouldChangePassword;
		authTokenService.saveShouldChangePassword(shouldChangePassword);
	}
	return shouldChangePassword;
}

/**
 * Queries the authentication API to determine if the user should change their password.
 * @param {UUID} userId - the user's id
 * @returns {Promise} - the promise response from axios
 */
function __getShouldChangePassword (userId) {
	return axios.get(utilityService.getApiAuthUrl(`/users/${userId}/shouldchangepassword`),
		{
			headers: utilityService.getHeaders(false)
		}
	);
}
